import React, { createContext, type ReactElement, type ReactNode, useState } from 'react';
import EventBus from '../services/EventBus';
import { EventBusEventEnum } from '../enums/EventBusEventEnum';

export interface RightPanelContextProps {
  rightPanelContent: ReactNode;
  setRightPanelContent: React.Dispatch<React.SetStateAction<ReactNode>>;
  isRightPanelOpen: boolean;
  toggleRightPanel: () => void;
  openRightPanel: (closeButtonVisible?: boolean) => void;
  closeRightPanel: () => void;
  setHasRightPanelCloseButton: React.Dispatch<React.SetStateAction<boolean>>;
  hasRightPanelCloseButton: boolean;
  setIsRightPanelCheckDirtyOnClose: React.Dispatch<React.SetStateAction<boolean>>;
  isRightPanelCheckDirtyOnClose: boolean;
  onRightPanelClose?: () => void;
}

interface RightPanelProviderProps {
  children: ReactNode;
}

export const RightPanelContext = createContext<RightPanelContextProps | undefined>(undefined);

export const RightPanelProvider = ({ children }: RightPanelProviderProps): ReactElement => {
  const [rightPanelContent, setRightPanelContent] = useState<ReactNode>(null);
  const [isRightPanelOpen, setIsRightPanelOpen] = useState<boolean>(false);
  const [isRightPanelCheckDirtyOnClose, setIsRightPanelCheckDirtyOnClose] = useState<boolean>(true);
  const [hasRightPanelCloseButton, setHasRightPanelCloseButton] = useState<boolean>(true);

  /**
   * Toggles the right panel by updating the state and emitting the corresponding event.
   *
   * @function toggleRightPanel
   */
  const toggleRightPanel = () => {
    setIsRightPanelOpen((prevState) => !prevState);
    // Send correct event
    if (isRightPanelOpen) {
      EventBus.emit(EventBusEventEnum.RightPanelClose, null);
    } else {
      EventBus.emit(EventBusEventEnum.RightPanelOpen, null);
    }
  };

  /**
   * @desc Open the right panel
   */
  const openRightPanel = (closeButtonVisible = true) => {
    setIsRightPanelOpen(true);
    setHasRightPanelCloseButton(closeButtonVisible);
    EventBus.emit(EventBusEventEnum.RightPanelOpen, null);
  };

  /**
   * @desc Open the right panel
   */
  const closeRightPanel = () => {
    setIsRightPanelOpen(false);
    EventBus.emit(EventBusEventEnum.RightPanelClose, null);
  };

  return (
    <RightPanelContext.Provider
      value={{
        closeRightPanel,
        hasRightPanelCloseButton,
        isRightPanelCheckDirtyOnClose,
        isRightPanelOpen,
        openRightPanel,
        rightPanelContent,
        setHasRightPanelCloseButton,
        setIsRightPanelCheckDirtyOnClose,
        setRightPanelContent,
        toggleRightPanel,
      }}
    >
      {children}
    </RightPanelContext.Provider>
  );
};
